import { supabase } from '@/integrations/supabase/client';
import type { PersonalWritingStyle, WritingStyleAnalysisResult } from '@/types';
import { upsertWritingIdentityFromPersonalStyle } from '@/api/writingIdentities';

/**
 * Get all writing styles for the current user
 */
export async function getWritingStyles(): Promise<PersonalWritingStyle[]> {
  const { data, error } = await supabase
    .from('personal_writing_styles')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as unknown as PersonalWritingStyle[];
}

/**
 * Get a specific writing style by ID
 */
export async function getWritingStyle(id: string): Promise<PersonalWritingStyle> {
  const { data, error } = await supabase
    .from('personal_writing_styles')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as unknown as PersonalWritingStyle;
}

/**
 * Analyze writing samples from URLs and text samples using AI
 */
export async function analyzeWritingSamples(
  urls: string[],
  textSamples: string[],
  languageCue?: string,
  sitemapUrls?: string[],
): Promise<WritingStyleAnalysisResult> {
  try {
    // Filter out empty values
    const validUrls = urls.filter(url => url.trim());
    const validTextSamples = textSamples.filter(sample => sample.trim());

    if (validUrls.length === 0 && validTextSamples.length === 0) {
      throw new Error('Please provide at least one URL or text sample for analysis');
    }

    const requestBody: any = {
      urls: validUrls,
      textSamples: validTextSamples,
      sitemapUrls: (sitemapUrls || []).filter((url) => url.trim()).slice(0, 10),
    };

    // Add language cue if provided
    if (languageCue && languageCue.trim()) {
      requestBody.languageCue = languageCue.trim();
    }

    const { data, error } = await supabase.functions.invoke('analyze-writing-style', {
      body: requestBody,
    });

    if (error) {
      console.error('[analyzeWritingSamples] Error:', error);
      throw new Error(error.message || 'Failed to analyze writing style');
    }

    return data;
  } catch (error) {
    console.error('[analyzeWritingSamples] Error:', error);
    throw error;
  }
}

/**
 * Create a new writing style
 */
export async function createWritingStyle(
  style: Omit<PersonalWritingStyle, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<PersonalWritingStyle> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('User not authenticated');
  const user = session.user;

  const { data, error } = await supabase
    .from('personal_writing_styles')
    .insert([{ ...style, user_id: user.id } as any])
    .select()
    .single();

  if (error) throw error;
  const created = data as unknown as PersonalWritingStyle;

  // Gradual v2 rollout: keep v1 write and mirror to writing_identities.
  void upsertWritingIdentityFromPersonalStyle(created);

  return created;
}

/**
 * Update an existing writing style
 */
export async function updateWritingStyle(
  id: string,
  updates: Partial<Omit<PersonalWritingStyle, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
): Promise<PersonalWritingStyle> {
  const { data, error } = await supabase
    .from('personal_writing_styles')
    .update(updates as any)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  const updated = data as unknown as PersonalWritingStyle;
  void upsertWritingIdentityFromPersonalStyle(updated);
  return updated;
}

/**
 * Delete a writing style
 */
export async function deleteWritingStyle(id: string): Promise<void> {
  const { error } = await supabase
    .from('personal_writing_styles')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Set default purpose for a writing style
 */
export async function setDefaultPurpose(id: string, purpose: string): Promise<PersonalWritingStyle> {
  const { data, error } = await supabase
    .from('personal_writing_styles')
    .update({ default_purpose: purpose || null })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  const updated = data as unknown as PersonalWritingStyle;
  void upsertWritingIdentityFromPersonalStyle(updated);
  return updated;
}
